import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";
import { useAtom } from "jotai";
import { differenceInYears, format, isBefore } from "date-fns";
import { useCrefazContext } from "@/app/contexts/CrefazContext";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import { useGenders } from "@/app/hooks/useGenders";
import { useMaritalStatus } from "@/app/hooks/useMaritalStatus";
import { BasicInput } from "@/view/components/BasicInput";
import { BasicSelect } from "@/view/components/BasicSelect";
import { Button } from "@/view/components/ui/button";
import { currentStep } from "@/app/store";
import masks from "@/app/utils/masks";
import { phoneMask } from "@/app/utils/phoneFormater";
import { states } from "@/app/utils/states";
import { orgs } from "@/app/utils/orgs";
import { dateMask } from "@/app/utils/dateFormater";

interface PersonalInfoFormProps {
  visible?: boolean;
}

export const personalFormSchema = z.object({
  name: z.string().min(1, "* obrigatório"),
  birthDate: z
    .string()
    .min(1, "* obrigatório")
    .refine((f) => {
      const [day, month, year] = f.split("/");
      const date = new Date(`${year}-${month}-${day}`);
      const diff = differenceInYears(new Date(), date);
      return diff >= 18 && diff < 85;
    }, "A idade deve estar entre 18 e 84 anos")
    .transform((f) => {
      const [day, month, year] = f.split("/");
      return new Date(`${year}-${month}-${day}`).toISOString();
    }),
  cpf: z
    .string()
    .optional()
    .transform((v) => v?.replace(/\D/g, "") ?? ""),
  phone: z
    .string()
    .transform((val) => val.replace(/\D/g, ""))
    .refine((val) => val.length === 11, "* telefone inválido"),
  email: z.string().min(1, "* obrigatório").email("email invalido"),
  gender: z.string().min(1, "* obrigatório"),
  maritalStatus: z.string().min(1, "* obrigatório"),
  spouseName: z.string().optional(),
  motherName: z.string().min(1, "* obrigatório"),
  placeOfBirth: z.object({
    city: z.string().min(1, "* obrigatório"),
    uf: z.string().min(1, "* obrigatório"),
  }),
  rg: z.object({
    number: z.string().min(1, "* obrigatório"),
    date: z
      .string()
      .min(1, "* obrigatório")
      .refine((f) => {
        const [day, month, year] = f.split("/");
        if (!day || !month || !year) return false;
        const date = new Date(`${year}-${month}-${day}`);
        return !isNaN(date.getTime()) && isBefore(date, new Date());
      }, "data inválida")
      .transform((f) => {
        const [day, month, year] = f.split("/");
        return new Date(`${year}-${month}-${day}`).toISOString();
      }),
    uf: z
      .string({ required_error: "* obrigatório" })
      .min(2, "* obrigatório")
      .max(2),
    org: z.string({ required_error: "* obrigatório" }).min(1, "* obrigatório"),
  }),
});

export default function PersonalInfoForm({ visible }: PersonalInfoFormProps) {
  const [, setStep] = useAtom(currentStep);
  const { customerData, setCustomerData } = useCrefazContext();
  const { data: isCustomer } = useCustomerCpf(customerData?.cpf);

  useEffect(() => {
    if (isCustomer) {
      setCustomerData({
        ...customerData,
        ...isCustomer,
        id: isCustomer.id,
      });
    }
  }, [isCustomer]);

  const { data: gendersData } = useGenders();
  const { data: maritalStatusData } = useMaritalStatus();

  const genderOptions =
    gendersData?.data.map((gender) => ({
      label: gender.nome,
      value: String(gender.id),
    })) ?? [];

  const maritalStatusOptions =
    maritalStatusData?.data.map((gender) => ({
      label: gender.nome,
      value: String(gender.id),
    })) ?? [];

  const {
    reset,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();

  const selectedMaritalStatus = watch("personalInfo.maritalStatus");

  const requiresSpouseName =
    maritalStatusData?.data.find(
      (status) => String(status.id) === selectedMaritalStatus,
    )?.exigeConjuge ?? false;

  useEffect(() => {
    if (!requiresSpouseName) {
      setValue("personalInfo.spouseName", "");
    }
  }, [requiresSpouseName]);

  useEffect(() => {
    if (!customerData) return;

    const apiData = isCustomer;

    const baseData = {
      name: apiData?.name || customerData.name,
      cpf: masks.cpf(apiData?.cpf || customerData.cpf),
      birthDate: apiData?.birthDate
        ? format(new Date(apiData.birthDate), "dd/MM/yyyy")
        : customerData.birthDate
          ? format(new Date(customerData.birthDate), "dd/MM/yyyy")
          : "",
      phone: apiData?.phone || customerData.phone || "",
      email: apiData?.email || customerData.email || "",
    };

    const additionalData = apiData
      ? {
          gender: apiData.gender ?? "",
          maritalStatus: apiData.maritalStatus ?? "",
          spouseName: apiData.spouseName ?? "",
          motherName: apiData.motherName ?? "",
          placeOfBirth: {
            city: apiData.placeOfBirth?.city ?? "",
            uf: apiData.placeOfBirth?.uf ?? "",
          },
          rg: {
            number: apiData.rg?.number ?? "",
            date: apiData.rg?.date
              ? format(new Date(apiData.rg.date), "dd/MM/yyyy")
              : "",
            uf: apiData.rg?.uf ?? "",
            org: apiData.rg?.org ?? "",
          },
        }
      : {
          email: customerData.email ?? "",
          gender: "",
          maritalStatus: "",
          spouseName: "",
          motherName: "",
          placeOfBirth: {
            city: "",
            uf: "",
          },
          rg: {
            number: "",
            date: "",
            uf: "",
            org: "",
          },
        };

    reset(
      {
        ...getValues(),
        personalInfo: {
          ...baseData,
          ...additionalData,
        },
        ...(apiData?.address
          ? {
              address: {
                zipCode: masks.zipcode(apiData.address.zipCode || ""),
                street: apiData.address.street || "",
                number: apiData.address.number || "",
                complement: apiData.address.complement || "",
                neighborhood: apiData.address.neighborhood || "",
                city: apiData.address.city || "",
                state: apiData.address.state || "",
              },
            }
          : customerData.address
            ? {
                address: {
                  zipCode: masks.zipcode(customerData.address.zipCode || ""),
                  street: customerData.address.street || "",
                  number: customerData.address.number || "",
                  complement: customerData.address.complement || "",
                  neighborhood: customerData.address.neighborhood || "",
                  city: customerData.address.city || "",
                  state: customerData.address.state || "",
                },
              }
            : {}),
      },
      {
        keepDirtyValues: true,
      },
    );
  }, [isCustomer]);

  const handleNext = async () => {
    const isValid = await trigger("personalInfo");
    if (isValid) {
      setStep((state) => state + 1);
    }
  };

  return (
    <div className={visible ? "flex flex-col gap-8" : "hidden"}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BasicInput
          name="personalInfo.name"
          label="Nome completo"
          error={(errors.personalInfo as any)?.name?.message}
        />
        <BasicInput
          name="personalInfo.cpf"
          label="CPF"
          disabled
          error={(errors.personalInfo as any)?.cpf?.message}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BasicInput
            name="personalInfo.rg.number"
            label="RG"
            error={(errors.personalInfo as any)?.rg?.number?.message}
          />
          <BasicInput
            type="text"
            name="personalInfo.rg.date"
            label="Data de emissão"
            error={(errors.personalInfo as any)?.rg?.date?.message}
            onInput={dateMask}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BasicSelect
            name="personalInfo.rg.org"
            label="Orgão emissor"
            error={(errors.personalInfo as any)?.rg?.org?.message}
            options={orgs}
            placeholder="Selecione"
          />

          <BasicSelect
            label="Uf de emissão"
            name="personalInfo.rg.uf"
            options={states}
            placeholder="Selecione o seu estado"
            error={(errors.personalInfo as any)?.rg?.uf?.message}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BasicInput
            type="text"
            name="personalInfo.birthDate"
            label="Data de nascimento"
            error={(errors.personalInfo as any)?.birthDate?.message}
            onInput={dateMask}
          />
          <BasicInput
            name="personalInfo.phone"
            label="Telefone"
            error={(errors.personalInfo as any)?.phone?.message}
            onInput={phoneMask}
            maxLength={15}
          />
        </div>
        <BasicInput
          name="personalInfo.email"
          label="E-mail"
          error={(errors.personalInfo as any)?.email?.message}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BasicSelect
            name="personalInfo.gender"
            label="Gênero"
            error={(errors.personalInfo as any)?.gender?.message}
            options={genderOptions}
            placeholder="Selecione"
          />
          <BasicSelect
            name="personalInfo.maritalStatus"
            label="Estado civil"
            error={(errors.personalInfo as any)?.maritalStatus?.message}
            options={maritalStatusOptions}
            placeholder="Selecione"
          />
        </div>
        <BasicInput
          name="personalInfo.spouseName"
          label="Nome do Cônjuge"
          disabled={!requiresSpouseName}
          error={(errors.personalInfo as any)?.spouseName?.message}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BasicSelect
            name="personalInfo.placeOfBirth.uf"
            label="UF de naturalidade"
            error={(errors.personalInfo as any)?.placeOfBirth?.uf?.message}
            options={states}
            placeholder="Selecione"
          />
          <BasicInput
            name="personalInfo.placeOfBirth.city"
            label="Cidade de naturalidade"
            error={(errors.personalInfo as any)?.placeOfBirth?.city?.message}
          />
        </div>
        <BasicInput
          name="personalInfo.motherName"
          label="Nome da mae"
          error={(errors.personalInfo as any)?.motherName?.message}
        />
      </div>
      <div className="flex justify-end">
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          type="button"
          onClick={handleNext}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
